<template>
    <v-container fluid class="org-page pa-4 pa-sm-6">
        <!-- ─────────────────────────  Page header  ───────────────────────── -->
        <header class="org-hero mb-5">
            <span class="org-hero__badge">
                <v-icon dark>mdi-sitemap</v-icon>
            </span>
            <div class="org-hero__text">
                <div class="org-hero__eyebrow">{{ tr('SystemAdmin', 'System Administration', 'إدارة النظام') }}</div>
                <h1 class="org-hero__title">{{ tr('ManageOrganization', 'Organizational Structure', 'الهيكل التنظيمي') }}</h1>
            </div>
            <div class="org-hero__stats">
                <span class="org-hero__stat">
                    <v-icon small dark class="me-1">mdi-file-tree</v-icon>
                    <strong>{{ totalStructures }}</strong>
                    <span class="ms-1">{{ $t('Structures') }}</span>
                </span>
            </div>
        </header>

        <!-- ─────────────────────────  Shell + tabs  ───────────────────────── -->
        <v-card class="org-shell" flat :loading="loading">
            <v-tabs v-model="tab"
                    class="org-tabs"
                    background-color="transparent"
                    color="primary"
                    slider-size="3"
                    grow>
                <v-tab class="org-tab">
                    <v-icon class="me-2" small>mdi-sitemap</v-icon>
                    {{ $t('Structures') }}
                </v-tab>
                <v-tab class="org-tab" @change="listAdminUsers">
                    <v-icon class="me-2" small>mdi-account-group-outline</v-icon>
                    {{ $t('Users') }}
                </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="org-tabs-items">
                <!-- ══════════════  TAB 1 — Structures  ══════════════ -->
                <v-tab-item>
                    <div class="pa-4 pa-sm-5">
                        <v-row>
                            <!-- ── Tree panel ── -->
                            <v-col cols="12" md="5" lg="4">
                                <section class="org-panel" :aria-label="$t('Structures')">
                                    <div class="org-panel__head">
                                        <span class="org-panel__head-icon">
                                            <v-icon small>mdi-file-tree-outline</v-icon>
                                        </span>
                                        <h2 class="org-panel__title">{{ $t('Structures') }}</h2>
                                        <v-spacer />
                                        <v-btn depressed
                                               small
                                               color="primary"
                                               class="org-btn"
                                               @click="addNewStructure">
                                            <v-icon small class="me-1">mdi-folder-plus-outline</v-icon>
                                            {{ $t('Add') }}
                                        </v-btn>
                                    </div>

                                    <div class="org-panel__toolbar">
                                        <v-text-field v-model="search"
                                                      class="org-search"
                                                      dense
                                                      outlined
                                                      rounded
                                                      hide-details
                                                      clearable
                                                      prepend-inner-icon="mdi-magnify"
                                                      clear-icon="mdi-close-circle-outline"
                                                      :label="$t('Search')"
                                                      @input="handleSearch" />

                                        <div class="org-panel__toolbar-row">
                                            <div class="org-tree-actions">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn icon
                                                               small
                                                               class="org-icon-btn"
                                                               v-bind="attrs"
                                                               :aria-label="tr('ExpandAll', 'Expand all', 'توسيع الكل')"
                                                               v-on="on"
                                                               @click="expandAll">
                                                            <v-icon small>mdi-unfold-more-horizontal</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>{{ tr('ExpandAll', 'Expand all', 'توسيع الكل') }}</span>
                                                </v-tooltip>

                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn icon
                                                               small
                                                               class="org-icon-btn ms-2"
                                                               v-bind="attrs"
                                                               :aria-label="tr('CollapseAll', 'Collapse all', 'طي الكل')"
                                                               v-on="on"
                                                               @click="collapseAll">
                                                            <v-icon small>mdi-unfold-less-horizontal</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>{{ tr('CollapseAll', 'Collapse all', 'طي الكل') }}</span>
                                                </v-tooltip>
                                            </div>

                                            <v-switch v-model="onlyActive"
                                                      class="org-switch"
                                                      :label="$t('ShowDeactivatedStructures')"
                                                      dense
                                                      hide-details
                                                      inset
                                                      color="primary"
                                                      @change="listOrganization" />
                                        </div>
                                    </div>

                                    <div class="org-tree-scroll">
                                        <!-- not activatable: selection is tracked by selectedStructureId and shown
                                             by the node pill. Vuetify's own active cue paints across the whole row,
                                             toggle and indent columns included, which spills outside the pill. -->
                                        <v-treeview ref="tree"
                                                    class="org-tree"
                                                    :items="result"
                                                    :search="search"
                                                    :filter="filter"
                                                    item-key="id"
                                                    :open.sync="open">
                                            <template v-slot:label="{ item, leaf, open: isOpen }">
                                                <v-tooltip top open-delay="400">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <div class="org-node"
                                                             :class="nodeClass(item, leaf)"
                                                             :role="item.id ? 'button' : null"
                                                             :tabindex="item.id ? 0 : null"
                                                             :aria-current="item.id && item.id === selectedStructureId ? 'true' : null"
                                                             v-bind="attrs"
                                                             v-on="on"
                                                             @click="viewStructureDetails(item)"
                                                             @keydown.enter.prevent="viewStructureDetails(item)"
                                                             @keydown.space.prevent="viewStructureDetails(item)">
                                                            <span class="org-node__icon">
                                                                <v-icon small>{{ nodeIcon(item, leaf, isOpen) }}</v-icon>
                                                            </span>
                                                            <span class="org-node__text">{{ item.name }}</span>
                                                        </div>
                                                    </template>
                                                    <span>{{ item.name }}</span>
                                                </v-tooltip>
                                            </template>
                                        </v-treeview>

                                        <div v-if="isTreeEmpty" class="org-empty org-empty--sm">
                                            <span class="org-empty__icon">
                                                <v-icon large>mdi-file-tree-outline</v-icon>
                                            </span>
                                            <p class="org-empty__title">{{ $t('NoItemsToShow') }}</p>
                                        </div>
                                    </div>
                                </section>
                            </v-col>

                            <!-- ── Details panel ── -->
                            <v-col cols="12" md="7" lg="8">
                                <v-scroll-y-transition mode="out-in">
                                    <div v-if="show" key="details">
                                        <section class="org-panel org-detail mb-4">
                                            <div class="org-detail__body">
                                                <span class="org-detail__avatar">
                                                    <v-icon dark>mdi-office-building-outline</v-icon>
                                                </span>
                                                <div class="org-detail__text">
                                                    <div class="org-eyebrow">{{ $t('Structure') }}</div>
                                                    <h2 class="org-detail__title">{{ selectedStructureTitle }}</h2>
                                                </div>
                                                <div class="org-detail__actions">
                                                    <v-btn depressed
                                                           small
                                                           color="primary"
                                                           class="org-btn"
                                                           @click="editStructure">
                                                        <v-icon small class="me-1">mdi-pencil-outline</v-icon>
                                                        {{ $t('Edit') }}
                                                    </v-btn>
                                                    <v-btn depressed
                                                           small
                                                           color="error"
                                                           class="org-btn ms-2"
                                                           @click="confirmDeleteStructure">
                                                        <v-icon small class="me-1">mdi-trash-can-outline</v-icon>
                                                        {{ $t('Delete') }}
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </section>

                                        <section class="org-panel">
                                            <div class="org-panel__head">
                                                <span class="org-panel__head-icon">
                                                    <v-icon small>mdi-account-multiple-outline</v-icon>
                                                </span>
                                                <h2 class="org-panel__title">{{ $t('UsersInStructure') }}</h2>
                                                <span class="org-count">{{ users.length }}</span>
                                                <v-spacer />
                                                <v-btn depressed
                                                       small
                                                       color="secondary"
                                                       class="org-btn white--text"
                                                       @click="openUserAddDialog()">
                                                    <v-icon small class="me-1">mdi-account-plus-outline</v-icon>
                                                    {{ $t('AddUser') }}
                                                </v-btn>
                                            </div>

                                            <v-data-table class="org-table"
                                                          dense
                                                          :headers="headers"
                                                          :items="users"
                                                          :items-per-page="10"
                                                          :no-data-text="$t('NoItemsToShow')">
                                                <template v-slot:[`item.fullname`]="{ item }">
                                                    <div class="org-user">
                                                        <span class="org-user__avatar">{{ initials(item.fullname) }}</span>
                                                        <span class="org-user__name">{{ item.fullname }}</span>
                                                    </div>
                                                </template>
                                                <template v-slot:[`item.roleName`]="{ item }">
                                                    <span v-if="item.roleName" class="org-chip">{{ item.roleName }}</span>
                                                    <span v-else class="org-muted">—</span>
                                                </template>
                                                <template v-slot:[`item.options`]="{ item }">
                                                    <v-tooltip top>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-btn icon
                                                                   x-small
                                                                   class="org-icon-btn org-icon-btn--danger"
                                                                   v-bind="attrs"
                                                                   :aria-label="$t('Delete')"
                                                                   v-on="on"
                                                                   @click="removeUserFromStructure(item.id)">
                                                                <v-icon small>mdi-trash-can-outline</v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>{{ $t('Delete') }}</span>
                                                    </v-tooltip>
                                                </template>
                                            </v-data-table>
                                        </section>
                                    </div>

                                    <!-- nothing selected yet -->
                                    <section v-else key="placeholder" class="org-panel org-panel--ghost">
                                        <div class="org-empty">
                                            <span class="org-empty__icon">
                                                <v-icon x-large>mdi-sitemap-outline</v-icon>
                                            </span>
                                            <p class="org-empty__title">
                                                {{ tr('SelectStructure', 'Select a structure', 'اختر هيكلاً تنظيمياً') }}
                                            </p>
                                            <p class="org-empty__hint">
                                                {{ tr('SelectStructureHint', 'Pick a node from the tree to view its details and members.', 'اختر عنصراً من الشجرة لعرض تفاصيله والمستخدمين المرتبطين به.') }}
                                            </p>
                                        </div>
                                    </section>
                                </v-scroll-y-transition>
                            </v-col>
                        </v-row>
                    </div>
                </v-tab-item>

                <!-- ══════════════  TAB 2 — Users  ══════════════ -->
                <v-tab-item>
                    <div class="pa-4 pa-sm-5">
                        <section class="org-panel org-filterbar mb-4">
                            <v-icon class="org-filterbar__icon">mdi-filter-variant</v-icon>
                            <UserAutocomplete v-model="selectedUserId"
                                              class="org-filterbar__field"
                                              :active="onlyActiveUsers"
                                              :label="$t('SelectUserSearch')" />
                            <div class="org-filterbar__actions">
                                <v-btn depressed small color="primary" class="org-btn" @click="searchUser">
                                    <v-icon small class="me-1">mdi-account-search-outline</v-icon>
                                    {{ $t('Search') }}
                                </v-btn>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon
                                               small
                                               class="org-icon-btn ms-2"
                                               v-bind="attrs"
                                               :aria-label="tr('Reset', 'Reset', 'إعادة تعيين')"
                                               v-on="on"
                                               @click="listAdminUsers(); selectedUserId = null">
                                            <v-icon small>mdi-refresh</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ tr('Reset', 'Reset', 'إعادة تعيين') }}</span>
                                </v-tooltip>
                            </div>
                        </section>

                        <section class="org-panel">
                            <div class="org-panel__head">
                                <span class="org-panel__head-icon">
                                    <v-icon small>mdi-account-group-outline</v-icon>
                                </span>
                                <h2 class="org-panel__title">{{ $t('Users') }}</h2>
                                <span class="org-count">{{ totalCount }}</span>
                                <v-spacer />
                                <v-switch v-model="onlyActiveUsers"
                                          class="org-switch me-4"
                                          dense
                                          hide-details
                                          inset
                                          color="primary"
                                          :label="$t('InactiveUsers')"
                                          @change="listAdminInactiveUsers" />
                                <v-btn depressed
                                       small
                                       color="primary"
                                       class="org-btn"
                                       @click="userDialog = true; isEditUser = false">
                                    <v-icon small class="me-1">mdi-account-plus-outline</v-icon>
                                    {{ $t('Add') }}
                                </v-btn>
                            </div>

                            <GridWithServerPaging class="org-table"
                                                  :headers="adminUserheaders"
                                                  :items="adminUsers"
                                                  :total="totalCount"
                                                  disable-sort
                                                  show-expand
                                                  @onChange="listAdminUsers"
                                                  @item-expanded="expand">
                                <template v-slot:[`item.fullnameAr`]="{ item }">
                                    <div class="org-user">
                                        <span class="org-user__avatar">{{ initials(item.fullnameAr || item.fullnameEn) }}</span>
                                        <span class="org-user__name">{{ item.fullnameAr }}</span>
                                    </div>
                                </template>
                                <template v-slot:[`item.approved`]="{ item }">
                                    <v-chip v-if="item.approved" small class="org-status org-status--ok">
                                        <v-icon x-small class="me-1">mdi-check-circle</v-icon>
                                        {{ $t('Active') }}
                                    </v-chip>
                                    <v-chip v-else small class="org-status org-status--off">
                                        <v-icon x-small class="me-1">mdi-close-circle</v-icon>
                                        {{ $t('InactiveUsers') }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.smsNotification`]="{ item }">
                                    <v-switch v-model="item.smsEnabled" class="org-switch org-switch--cell" dense inset hide-details color="primary" @change="enableSms(item)" />
                                </template>
                                <template v-slot:[`item.ldapEnabled`]="{ item }">
                                    <v-switch v-model="item.ldapEnabled" class="org-switch org-switch--cell" dense inset hide-details color="primary" @change="enableLdap(item)" />
                                </template>
                                <template v-slot:[`item.emailNotification`]="{ item }">
                                    <v-switch v-model="item.emailNotificationEnabled" class="org-switch org-switch--cell" dense inset hide-details color="primary" @change="enableEmail(item)" />
                                </template>

                                <template v-slot:expanded-item="{ headers: expandedHeaders, item }">
                                    <td :colspan="expandedHeaders.length" class="org-expanded">
                                        <v-progress-linear v-if="loadingRoles" color="primary" indeterminate reverse />
                                        <template v-if="item.structuresRoles && item.structuresRoles.length > 0">
                                            <v-simple-table dense class="org-subtable">
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr>
                                                            <th>{{ $t('Role') }}</th>
                                                            <th>{{ $t('Structure') }}</th>
                                                            <th>{{ $t('IsPrimary') }}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(row, index) in item.structuresRoles" :key="index">
                                                            <td><span class="org-chip">{{ row.roleName }}</span></td>
                                                            <td>{{ row.structureName }}</td>
                                                            <td>
                                                                <v-icon v-if="row.isPrimary" color="primary" small>mdi-check-circle</v-icon>
                                                                <v-icon v-else color="error" small>mdi-close-circle</v-icon>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </template>
                                        <span v-else class="org-muted">{{ $t('NoItemsToShow') }}</span>
                                    </td>
                                </template>

                                <template v-slot:[`item.options`]="{ item }">
                                    <div class="org-row-actions">
                                        <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       x-small
                                                       class="org-icon-btn"
                                                       v-bind="attrs"
                                                       :aria-label="$t('Edit')"
                                                       v-on="on"
                                                       @click="selectedUserId = item.id; userDialog = true; isEditUser = true">
                                                    <v-icon small>mdi-pencil-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('Edit') }}</span>
                                        </v-tooltip>

                                        <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       x-small
                                                       class="org-icon-btn ms-1"
                                                       v-bind="attrs"
                                                       :aria-label="tr('Privileges', 'Privileges', 'الصلاحيات')"
                                                       v-on="on"
                                                       @click="selectedUserId = item.id; userPermissionDialog = true">
                                                    <v-icon small>mdi-shield-half-full</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ tr('Privileges', 'Privileges', 'الصلاحيات') }}</span>
                                        </v-tooltip>

                                        <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       x-small
                                                       class="org-icon-btn ms-1"
                                                       v-bind="attrs"
                                                       :aria-label="$t('ResetPassword')"
                                                       v-on="on"
                                                       @click="selectedUserId = item.id; userResetPasswordDialog = true">
                                                    <v-icon small>mdi-lock-reset</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('ResetPassword') }}</span>
                                        </v-tooltip>
                                    </div>
                                </template>
                            </GridWithServerPaging>
                        </section>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </v-card>

        <!-- ─────────────────────────  Dialogs  ───────────────────────── -->
        <admin-structure-form v-if="structureDialog" v-model="structureDialog" :edit="isEditStructure" :structure-id="selectedStructureId" @update="updated" />
        <admin-user-form v-if="userDialog" v-model="userDialog" :user-id="selectedUserId" :edit="isEditUser" @update="userUpdated" />
        <user-permissions v-if="userPermissionDialog" v-model="userPermissionDialog" :user-id="selectedUserId" />
        <user-reset-password v-if="userResetPasswordDialog" v-model="userResetPasswordDialog" :user-id="selectedUserId" />

        <CommonDialog v-if="isStructureDelete"
                      v-model="isStructureDelete"
                      :fullscreen="false"
                      width="560"
                      :title="$t('DeleteStructure')"
                      show-action-toolbar>
            <div class="org-dialog org-dialog--confirm">
                <span class="org-dialog__icon org-dialog__icon--danger">
                    <v-icon large color="error">mdi-alert-outline</v-icon>
                </span>
                <p class="org-dialog__question">{{ $t("ConfirmDelete") }}</p>
                <p v-if="selectedStructureTitle" class="org-dialog__subject">{{ selectedStructureTitle }}</p>
            </div>
            <template v-slot:actions>
                <v-btn depressed small color="error" class="org-btn" @click="deleteStructure">
                    <v-icon small class="me-1">mdi-trash-can-outline</v-icon>
                    {{ $t("Yes") }}
                </v-btn>
                <v-btn depressed small class="org-btn org-btn--muted ms-3" @click="isStructureDelete = false">
                    {{ $t("Cancel") }}
                </v-btn>
            </template>
        </CommonDialog>

        <v-form ref="userInStructureForm" v-model="valid" lazy-validation>
            <CommonDialog v-if="userAddDialog"
                          v-model="userAddDialog"
                          :fullscreen="false"
                          width="640"
                          :title="$t('AddUser')"
                          show-action-toolbar>
                <div class="org-dialog">
                    <p class="org-dialog__hint">
                        <v-icon small class="me-1">mdi-information-outline</v-icon>
                        {{ selectedStructureTitle }}
                    </p>
                    <v-row dense>
                        <v-col cols="12">
                            <UserAutocomplete v-model="selectedUserId" :rules="nameRules" required :label="$t('SelectUserSearch')" />
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete v-model="selectedRoleId"
                                            :rules="nameRules"
                                            required
                                            dense
                                            outlined
                                            prepend-inner-icon="mdi-shield-account-outline"
                                            item-text="name"
                                            item-value="id"
                                            :items="roles"
                                            :label="$t('SelectRole')" />
                        </v-col>
                    </v-row>
                </div>
                <template v-slot:actions>
                    <v-btn depressed small color="secondary" class="org-btn white--text" @click="saveUserToStructure">
                        <v-icon small class="me-1">mdi-check</v-icon>
                        {{ $t("Add") }}
                    </v-btn>
                    <v-btn depressed small class="org-btn org-btn--muted ms-3" @click="userAddDialog = false; selectedRoleId = null">
                        {{ $t("Cancel") }}
                    </v-btn>
                </template>
            </CommonDialog>
        </v-form>
    </v-container>
</template>

<script>
    import AdminStructureForm from '@/components/app/admin/AdminStructureForm'
    import UserPermissions from '@/components/app/admin/UserPermissions'
    import UserResetPassword from '@/components/app/admin/UserResetPassword'
    import AdminUserForm from '@/components/app/admin/AdminUserForm'
    import CommonDialog from '@/components/controls/CommonDialog'
    import UserAutocomplete from '@/components/controls/UserSelect'
    import GridWithServerPaging from '@/components/controls/GridWithServerPaging.vue'
    import StructuresService from '@/services/StructuresService.js'
    import LookupsService from '@/services/LookupsService.js'
    import UsersService from '@/services/UsersService.js'

    export default {
        components: {
            AdminStructureForm, UserPermissions, AdminUserForm, UserAutocomplete, CommonDialog, GridWithServerPaging, UserResetPassword
        },

        data() {
            return {
                show: false,
                valid: true,
                isStructureDelete: false,
                onlyActive: true,
                onlyActiveUsers: true,
                loadingRoles: false,
                userPermissionDialog: false,
                userResetPasswordDialog: false,
                userAddDialog: false,
                loading: true,
                structureDialog: false,
                userDialog: false,
                isEditStructure: false,
                isEditUser: false,
                selectedStructureTitle: '',
                result: [],
                users: [],
                adminUsers: [],
                roles: [],
                open: [1, 2],
                totalCount: 0,
                selectedStructureId: null,
                selectedUserId: null,
                tab: 0,
                search: null,
                selectedRoleId: null,
                options: {
                    page: 1, itemsPerPage: 10
                },
                headers: [
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('FullName'), value: 'fullname' },
                    { text: this.$t('Role'), value: 'roleName' },
                    { text: this.$t('Email'), value: 'email' },
                    { text: this.$t('Options'), value: 'options', sortable: false, align: 'center' },
                ],
                adminUserheaders: [
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('FullNameAr'), value: 'fullnameAr' },
                    { text: this.$t('FullNameEn'), value: 'fullnameEn' },
                    { text: this.$t('Username'), value: 'username' },
                    { text: this.$t('Email'), value: 'email' },
                    { text: this.$t('EnableSms'), value: 'smsNotification' },
                    { text: this.$t('EnableEmail'), value: 'emailNotification' },
                    { text: this.$t('LdapEnabled'), value: 'ldapEnabled' },
                    { text: this.$t('Approved'), value: 'approved' },
                    { text: this.$t('Options'), value: 'options', align: 'center' },
                ],
                nameRules: [
                    v => !!v || this.$t('RequiredField')
                ],
            }
        },

        computed: {
            filter() {
                return this.caseSensitive
                    ? (item, search, textKey) => item[textKey].indexOf(search) > -1
                    : undefined
            },

            // total number of real structures (the synthetic root node carries no id)
            totalStructures() {
                const count = nodes => (nodes || []).reduce((sum, node) => sum + (node.id ? 1 : 0) + count(node.children), 0)
                return count(this.result)
            },

            // true when there is nothing to render: no data at all, or no search match
            isTreeEmpty() {
                if (!this.result || this.result.length === 0) {
                    return true
                }
                if (!this.search) {
                    return false
                }
                const needle = String(this.search).toLocaleLowerCase()
                const matches = nodes => (nodes || []).some(node =>
                    String(node.name || '').toLocaleLowerCase().indexOf(needle) > -1 || matches(node.children))
                return !matches(this.result)
            },
        },

        watch: {
            userAddDialog: {
                handler: function (open) {
                    if (open) {
                        this.listRoles()
                    }
                }
            }
        },

        created() {
            this.listOrganization()
        },

        methods: {

            // translate with a built-in fallback, so newly introduced labels still read
            // correctly when the key has not been added to the translations table yet
            tr(key, fallbackEn, fallbackAr) {
                if (this.$te(key)) {
                    return this.$t(key)
                }
                return this.$i18n.locale === 'ar' ? fallbackAr : fallbackEn
            },

            nodeClass(item, leaf) {
                return {
                    'org-node--root': !item.id,
                    'org-node--branch': !!item.id && !leaf,
                    'org-node--leaf': !!item.id && leaf,
                    'org-node--selected': !!item.id && item.id === this.selectedStructureId
                }
            },

            nodeIcon(item, leaf, open) {
                if (!item.id) {
                    return 'mdi-home-city-outline'
                }
                if (leaf) {
                    return 'mdi-office-building-outline'
                }
                return open ? 'mdi-folder-open-outline' : 'mdi-folder-outline'
            },

            initials(name) {
                if (!name) {
                    return '?'
                }
                return String(name).trim().charAt(0).toLocaleUpperCase()
            },

            expandAll() {
                if (this.$refs.tree) {
                    this.$refs.tree.updateAll(true)
                }
            },

            collapseAll() {
                if (this.$refs.tree) {
                    this.$refs.tree.updateAll(false)
                }
            },

            handleSearch(input) {
                if (input) {
                    this.$refs.tree.updateAll(true)
                } else {
                    this.$refs.tree.updateAll(false)
                }
            },
            openUserAddDialog() {
                this.selectedUserId = null
                this.selectedRoleId = null
                this.userAddDialog = true
            },

            addNewStructure() {
                this.isEditStructure = false
                this.structureDialog = true
            },

            updated(value) {
                if (value) {
                    this.listOrganization()
                }
            },

            userUpdated(value) {
                if (value) {
                    this.listAdminUsers()
                }
            },

            listOrganization() {
                StructuresService.listOrganization(this.onlyActive).then((res) => {
                    if (res.data) {
                        this.result = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            listRoles() {
                LookupsService.listRoles().then((res) => {
                    if (res.data) {
                        this.roles = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            viewStructureDetails(item) {
                if (item.id) {
                    this.show = true
                    this.selectedStructureId = item.id
                    this.selectedStructureTitle = item.name
                    this.listUsersInStructure(item.id)
                }
            },

            listUsersInStructure(structureId) {
                StructuresService.listUsersInStructure(structureId).then((res) => {
                    if (res.data) {
                        this.users = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            saveUserToStructure() {
                if (this.$refs.userInStructureForm.validate()) {
                    StructuresService.addUserToStructure(this.selectedStructureId, this.selectedUserId, this.selectedRoleId).then((res) => {
                        if (res.success) {
                            this.userAddDialog = false
                            this.listUsersInStructure(this.selectedStructureId)
                        }
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            editStructure() {
                this.isEditStructure = true
                this.structureDialog = true
            },
            confirmDeleteStructure() {
                this.isStructureDelete = true
            },
            async deleteStructure() {
                await StructuresService.deleteStructure(this.selectedStructureId)
                this.isStructureDelete = false
                // the deleted node is gone from the tree, so close its details panel too
                this.show = false
                this.selectedStructureId = null
                this.selectedStructureTitle = ''
                this.users = []
                this.listOrganization()
            },
            enableSms(user) {
                if (user) {
                    UsersService.enableSms(user.id, user.smsEnabled)
                        .then(() => {
                            this.$root.$notification(user.smsEnabled == true ? this.$t('SmsEnabled') : this.$t('SmsDisabled'), 'success')
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },

            enableLdap(user) {
                if (user) {
                    UsersService.enableLdap(user.id, user.ldapEnabled)
                        .then(() => {
                            this.$root.$notification(user.ldapEnabled == true ? this.$t('LdapIsEnabled') : this.$t('LdapIsDisabled'), 'success')
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },

            enableEmail(user) {
                if (user) {
                    UsersService.enableEmail(user.id, user.emailNotificationEnabled)
                        .then(() => {
                            this.$root.$notification(user.smsEnabled == true ? this.$t('emailEnabled') : this.$t('emailDisabled'), 'success')
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },

            async removeUserFromStructure(userId) {
                if (await this.$root.$confirm(this.$t('Delete'), this.$t('ConfirmDelete'))) {
                    this.loading = true
                    StructuresService.removeUserFromStructure(this.selectedStructureId, userId)
                        .then(() => {
                            this.listUsersInStructure(this.selectedStructureId)
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
            },

            listAdminInactiveUsers() {
                this.listAdminUsers()
            },

            listAdminUsers(options) {
                const page = options != undefined || options == false ? options.page : 1
                const pageSize = options != undefined ? options.itemsPerPage : 10
                let active = this.onlyActiveUsers
                this.loading = true
                UsersService.listAdminUsers(page, pageSize, active)
                    .then((res) => {
                        if (res.data) {
                            this.adminUsers = res.data.data
                            this.totalCount = res.data.total
                        }
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => this.loading = false)
            },

            expand(data) {
                if (data.value == true) {
                    this.loadingRoles = true
                    UsersService.listUserRolesStructures(data.item.id)
                        .then((res) => {
                            if (res.data) {
                                data.item.structuresRoles = res.data
                            }
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loadingRoles = false)
                }
            },

            searchUser() {

                if (this.selectedUserId) {
                    this.loading = true
                    UsersService.getUser(this.selectedUserId)
                        .then((res) => {
                            this.adminUsers = []
                            this.adminUsers.push(res.data)
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => this.loading = false)
                }
                else {
                    this.$root.$notification(this.$t('SelectUser'), 'error')
                }
            }
        },
    }
</script>

<style>
    /* ==========================================================================
       Organizational Structure page
       Every rule is namespaced under .org-page so nothing leaks into other pages.
       Direction-dependent rules use Vuetify's .v-application--is-ltr / --is-rtl.
       ========================================================================== */

    .org-page {
        --org-surface: #ffffff;
        --org-border: #e4e9ee;
        --org-line: #d7dfe5;
        --org-text: #2b3a42;
        --org-muted: #78899a;
        --org-radius: 14px;
        --org-radius-sm: 10px;
        --org-shadow: 0 1px 2px rgba(16, 36, 44, .04), 0 6px 20px rgba(16, 36, 44, .06);
        /* The pale cyan on this page was never a literal in the source: it is
           --v-primary-lighten5, which Vuetify derives from the theme's primary colour
           and which resolves to #b5ffff here. The theme is shared by the whole app, so
           it is left untouched and the value is overridden locally instead. */
        --org-accent: #84a6a5;
        /* Same colour as a state layer. #84a6a5 at full strength behind the existing
           teal text measures 2.22:1, so transient hover/focus/selected-row washes use it
           at 18% over white (#e9efef): 5.02:1 for the teal, matching the 5.20:1 the old
           #b5ffff gave, and 1.16:1 against white so the wash stays as light as before. */
        --org-tint: rgba(132, 166, 165, .18);
        /* Readable foreground on the solid accent: 5.85:1. */
        --org-on-accent: #002a29;
        max-width: 1680px;
        margin: 0 auto;
    }

    /* ---------- page header ---------- */
    .org-page .org-hero {
        position: relative;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;
        padding: 18px 24px;
        border-radius: var(--org-radius);
        background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-secondary-base) 100%);
        box-shadow: var(--org-shadow);
        color: #fff;
        overflow: hidden;
    }

    .org-page .org-hero::after {
        content: '';
        position: absolute;
        top: -70px;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: rgba(255, 255, 255, .09);
        pointer-events: none;
    }

    .v-application--is-ltr .org-page .org-hero::after {
        right: -50px;
    }

    .v-application--is-rtl .org-page .org-hero::after {
        left: -50px;
    }

    .org-page .org-hero__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 12px;
        background: rgba(255, 255, 255, .18);
        border: 1px solid rgba(255, 255, 255, .25);
        flex: 0 0 48px;
    }

    .org-page .org-hero__text {
        min-width: 0;
    }

    .org-page .org-hero__eyebrow {
        font-size: 12px;
        letter-spacing: .04em;
        text-transform: uppercase;
        opacity: .85;
        line-height: 1.4;
    }

    .org-page .org-hero__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 22px;
        font-weight: 600;
        line-height: 1.3;
        margin: 0;
        color: #fff;
    }

    .org-page .org-hero__stats {
        margin-inline-start: auto;
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;
        z-index: 1;
    }

    .org-page .org-hero__stat {
        display: inline-flex;
        align-items: center;
        padding: 7px 14px;
        border-radius: 999px;
        background: rgba(255, 255, 255, .16);
        border: 1px solid rgba(255, 255, 255, .22);
        font-size: 13px;
        white-space: nowrap;
    }

    /* ---------- shell & tabs ---------- */
    .org-page .org-shell {
        border-radius: var(--org-radius);
        border: 1px solid var(--org-border);
        box-shadow: var(--org-shadow);
        background: var(--org-surface);
        overflow: hidden;
    }

    .org-page .org-tabs {
        border-bottom: 1px solid var(--org-border);
        background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
    }

    .org-page .org-tab {
        text-transform: none;
        letter-spacing: 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--org-muted) !important;
        min-height: 56px;
    }

    .org-page .org-tab.v-tab--active {
        color: var(--v-primary-base) !important;
    }

    .org-page .org-tabs-items {
        background: #f7f9fa;
    }

    /* ---------- generic panel ---------- */
    .org-page .org-panel {
        background: var(--org-surface);
        border: 1px solid var(--org-border);
        border-radius: var(--org-radius);
        box-shadow: 0 1px 2px rgba(16, 36, 44, .04);
        overflow: hidden;
    }

    .org-page .org-panel--ghost {
        background: transparent;
        border-style: dashed;
        box-shadow: none;
    }

    .org-page .org-panel__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--org-border);
        background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
    }

    .org-page .org-panel__head-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 9px;
        background: var(--org-accent);
        flex: 0 0 30px;
    }

    .org-page .org-panel__head-icon .v-icon {
        color: var(--org-on-accent) !important;
    }

    .org-page .org-panel__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--org-text);
        margin: 0;
        line-height: 1.4;
    }

    .org-page .org-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 26px;
        height: 22px;
        padding: 0 8px;
        border-radius: 999px;
        background: var(--org-accent);
        color: var(--org-on-accent);
        font-size: 12px;
        font-weight: 600;
    }

    .org-page .org-eyebrow {
        font-size: 11px;
        letter-spacing: .05em;
        text-transform: uppercase;
        color: var(--org-muted);
        line-height: 1.4;
    }

    .org-page .org-muted {
        color: var(--org-muted);
    }

    /* ---------- buttons ---------- */
    .org-page .org-btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-size: 14px !important;
        font-weight: 600;
        min-width: 0;
        padding: 0 14px;
    }

    .org-page .org-btn--muted {
        background: #eef1f4 !important;
        color: var(--org-text) !important;
    }

    /* icon-only buttons: they fill with colour on hover/focus because the global
       stylesheet forces icons inside a hovered/focused button to white */
    .org-page .org-icon-btn {
        border-radius: 9px;
        background: #eff3f6;
        transition: background .18s ease;
    }

    .org-page .org-icon-btn .v-icon {
        color: var(--org-muted);
    }

    .org-page .org-icon-btn:hover,
    .org-page .org-icon-btn:focus {
        background: var(--v-primary-base);
    }

    .org-page .org-icon-btn--danger:hover,
    .org-page .org-icon-btn--danger:focus {
        background: var(--v-error-base);
    }

    .org-page .org-row-actions {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    /* ---------- tree panel ---------- */
    .org-page .org-panel__toolbar {
        padding: 14px 16px 6px;
        border-bottom: 1px solid var(--org-border);
    }

    .org-page .org-panel__toolbar-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 10px;
    }

    .org-page .org-tree-actions {
        display: inline-flex;
        align-items: center;
    }

    .org-page .org-panel__toolbar-row .org-switch {
        margin-inline-start: auto;
        margin-top: 0;
        padding-top: 0;
    }

    .org-page .org-switch .v-label {
        font-size: 13px;
        color: var(--org-muted);
    }

    .org-page .org-switch--cell {
        display: inline-flex;
    }

    .org-page .org-search .v-input__slot {
        background: #f7f9fa !important;
    }

    .org-page .org-tree-scroll {
        max-height: calc(100vh - 420px);
        min-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 10px 12px 16px;
    }

    .org-page .org-tree-scroll::-webkit-scrollbar-thumb {
        background: var(--org-line) !important;
        border-radius: 4px;
    }

    /* treeview internals */
    .org-page .org-tree .v-treeview-node__root {
        min-height: 42px;
        padding: 0;
    }

    .org-page .org-tree .v-treeview-node__content {
        margin: 0;
    }

    .org-page .org-tree .v-treeview-node__label {
        flex: 1 1 auto;
        overflow: visible;
        font-size: 14px;
    }

    .org-page .org-tree .v-treeview-node__toggle {
        color: var(--org-muted);
        transition: transform .2s ease, color .2s ease;
    }

    .org-page .org-tree .v-treeview-node__toggle:hover {
        color: var(--v-primary-base);
    }

    .org-page .org-tree .v-treeview-node__children {
        position: relative;
    }

    /* Vertical guide lines that visualise the hierarchy.
       A treeview node is indented by 24px level spacers, and the toggle column is
       also 24px wide, so the guide for the first level sits at 12px (the centre of
       the parent's toggle) and every extra nesting level adds another 24px. */
    .org-page .org-tree .v-treeview-node__children::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 14px;
        width: 1px;
        background: var(--org-line);
        pointer-events: none;
    }

    .v-application--is-ltr .org-page .org-tree .v-treeview-node__children::before {
        left: 12px;
    }

    .v-application--is-ltr .org-page .org-tree .v-treeview-node__children .v-treeview-node__children::before {
        left: 36px;
    }

    .v-application--is-ltr .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        left: 60px;
    }

    .v-application--is-ltr .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        left: 84px;
    }

    .v-application--is-ltr .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        left: 108px;
    }

    .v-application--is-rtl .org-page .org-tree .v-treeview-node__children::before {
        right: 12px;
    }

    .v-application--is-rtl .org-page .org-tree .v-treeview-node__children .v-treeview-node__children::before {
        right: 36px;
    }

    .v-application--is-rtl .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        right: 60px;
    }

    .v-application--is-rtl .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        right: 84px;
    }

    .v-application--is-rtl .org-page .org-tree .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children .v-treeview-node__children::before {
        right: 108px;
    }

    /* node pill */
    .org-page .org-node {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 7px 10px;
        border-radius: var(--org-radius-sm);
        cursor: pointer;
        transition: background .18s ease, box-shadow .18s ease;
    }

    .org-page .org-node:hover {
        background: var(--org-tint);
    }

    .org-page .org-node:focus {
        outline: none;
        background: var(--org-tint);
        box-shadow: 0 0 0 2px var(--v-primary-base);
    }

    .org-page .org-node--selected,
    .org-page .org-node--selected:hover {
        background: var(--org-accent);
        box-shadow: inset 0 0 0 1px var(--v-primary-base);
    }

    .org-page .org-node--root {
        cursor: default;
        font-weight: 600;
    }

    .org-page .org-node--root:hover {
        background: transparent;
    }

    .org-page .org-node__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        border-radius: 9px;
        background: #eff3f6;
        transition: background .18s ease;
    }

    .org-page .org-node__icon .v-icon {
        font-size: 16px;
        color: var(--org-muted) !important;
    }

    .org-page .org-node--branch .org-node__icon {
        background: var(--org-accent);
    }

    .org-page .org-node--branch .org-node__icon .v-icon {
        color: var(--org-on-accent) !important;
    }

    .org-page .org-node--root .org-node__icon,
    .org-page .org-node--selected .org-node__icon {
        background: var(--v-primary-base);
    }

    .org-page .org-node--root .org-node__icon .v-icon,
    .org-page .org-node--selected .org-node__icon .v-icon {
        color: #fff !important;
    }

    .org-page .org-node__text {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--org-text);
        line-height: 1.5;
    }

    .org-page .org-node--selected .org-node__text {
        color: var(--org-on-accent);
        font-weight: 600;
    }

    /* ---------- details panel ---------- */
    .org-page .org-detail__body {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 14px;
        padding: 16px 18px;
    }

    .org-page .org-detail__avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 46px;
        height: 46px;
        flex: 0 0 46px;
        border-radius: 13px;
        background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-secondary-base) 100%);
    }

    .org-page .org-detail__text {
        min-width: 0;
        flex: 1 1 220px;
    }

    .org-page .org-detail__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 19px;
        font-weight: 600;
        color: var(--org-text);
        line-height: 1.35;
        margin: 0;
        word-break: break-word;
    }

    .org-page .org-detail__actions {
        display: flex;
        align-items: center;
        margin-inline-start: auto;
    }

    /* ---------- tables ---------- */
    .org-page .org-table thead th {
        background: #f7f9fa;
        white-space: nowrap;
        border-bottom: 1px solid var(--org-border) !important;
    }

    .org-page .org-table tbody tr {
        transition: background .15s ease;
    }

    .org-page .org-table tbody tr:hover {
        background: var(--org-tint) !important;
    }

    .org-page .org-table tbody td {
        border-bottom: 1px solid #f0f3f5 !important;
    }

    .org-page .org-user {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    .org-page .org-user__avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        flex: 0 0 26px;
        border-radius: 50%;
        background: var(--org-accent);
        color: var(--org-on-accent);
        font-size: 12px;
        font-weight: 700;
    }

    .org-page .org-user__name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .org-page .org-chip {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 999px;
        background: #eff3f6;
        color: var(--org-text);
        font-size: 12px;
        line-height: 20px;
        white-space: nowrap;
    }

    .org-page .org-status {
        font-size: 12px;
        font-weight: 600;
    }

    .org-page .org-status--ok {
        background: var(--org-accent) !important;
        color: var(--org-on-accent) !important;
    }

    .org-page .org-status--off {
        background: #f1f3f5 !important;
        color: var(--org-muted) !important;
    }

    .org-page .org-expanded {
        background: #fafcfd;
        padding: 12px 16px !important;
    }

    .org-page .org-subtable table {
        background: transparent;
    }

    .org-page .org-subtable th {
        font-weight: 600 !important;
    }

    /* ---------- filter bar (users tab) ---------- */
    .org-page .org-filterbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        padding: 14px 16px 4px;
    }

    .org-page .org-filterbar__icon {
        color: var(--org-muted) !important;
    }

    .org-page .org-filterbar__field {
        flex: 1 1 320px;
        max-width: 520px;
        min-width: 220px;
    }

    .org-page .org-filterbar__actions {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
    }

    /* ---------- empty states ---------- */
    .org-page .org-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 56px 24px;
        min-height: 320px;
    }

    .org-page .org-empty--sm {
        padding: 28px 16px;
        min-height: 0;
    }

    .org-page .org-empty__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 76px;
        height: 76px;
        border-radius: 50%;
        background: var(--org-accent);
        margin-bottom: 18px;
    }

    .org-page .org-empty--sm .org-empty__icon {
        width: 56px;
        height: 56px;
        margin-bottom: 12px;
    }

    .org-page .org-empty__icon .v-icon {
        color: var(--org-on-accent) !important;
    }

    .org-page .org-empty__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 16px;
        font-weight: 600;
        color: var(--org-text);
        margin: 0 0 6px;
    }

    .org-page .org-empty__hint {
        font-size: 14px;
        color: var(--org-muted);
        max-width: 380px;
        margin: 0;
        line-height: 1.6;
    }

    /* ---------- dialog content ---------- */
    .org-dialog {
        padding: 20px 20px 8px;
    }

    .org-dialog--confirm {
        text-align: center;
        padding: 28px 24px 12px;
    }

    .org-dialog__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 68px;
        height: 68px;
        border-radius: 50%;
        margin-bottom: 16px;
    }

    .org-dialog__icon--danger {
        background: rgba(183, 28, 28, .08);
    }

    .org-dialog__question {
        font-size: 16px;
        font-weight: 600;
        color: #2b3a42;
        margin: 0 0 6px;
    }

    .org-dialog__subject {
        font-size: 14px;
        color: #78899a;
        margin: 0;
    }

    .org-dialog__hint {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-radius: 10px;
        background: #f4f7f9;
        color: #78899a;
        font-size: 13px;
        margin-bottom: 8px;
    }

    .org-dialog .org-btn,
    .v-card__actions .org-btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 600;
    }

    /* ---------- responsive ---------- */
    @media (max-width: 959px) {
        .org-page .org-tree-scroll {
            max-height: 46vh;
            min-height: 220px;
        }

        .org-page .org-hero__stats {
            margin-inline-start: 0;
            width: 100%;
        }

        .org-page .org-empty {
            min-height: 220px;
            padding: 36px 20px;
        }
    }

    @media (max-width: 599px) {
        .org-page .org-hero {
            padding: 16px;
        }

        .org-page .org-hero__title {
            font-size: 18px;
        }

        .org-page .org-tab {
            font-size: 14px;
            min-height: 50px;
        }

        .org-page .org-panel__head {
            padding: 12px;
        }

        .org-page .org-detail__actions {
            width: 100%;
            margin-inline-start: 0;
        }

        .org-page .org-detail__actions .v-btn {
            flex: 1 1 auto;
        }

        .org-page .org-filterbar__actions {
            width: 100%;
            margin-bottom: 12px;
        }
    }
</style>
